import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { useFinalAmountWarning } from "@/hooks/useFinalAmountWarning";
import { postFinalAmount, postFinalAmounts, postFinalQuantities, postFinalQuantity } from "@/services/Orders";
import { getFinalAmountError } from "@/utils/apiErrors";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { toWeightQuantity, WeightInputValue } from "@/utils/weight";
import { assessmentKey } from "./assessmentLines";
import type { FinalAmountWarningModalProps } from "./index";

export type FinalQuantityEntry =
  | { orderId: number; weight: WeightInputValue } // kg/t orders: the new endpoints
  | { orderId: number; amount: number }; // other units: the legacy endpoints

type WeightEntry = Extract<FinalQuantityEntry, { weight: WeightInputValue }>;
type AmountEntry = Extract<FinalQuantityEntry, { amount: number }>;

const isWeightEntry = (entry: FinalQuantityEntry): entry is WeightEntry => "weight" in entry;
const isAmountEntry = (entry: FinalQuantityEntry): entry is AmountEntry => !isWeightEntry(entry);

export interface UseFinalQuantitySaveOptions {
  /** "single" posts one order to /finalQuantity; "batch" posts every weight entry to /finalQuantities at once. */
  mode: "single" | "batch";
  taskId?: number;
  /** Dialog labels by order id ("2041 · Papp"). */
  orderLabels?: Record<number, string>;
  /** Closes the task UI: after a successful save, and after a 191 conflict. */
  close: () => void;
}

interface SaveVariables {
  entries: FinalQuantityEntry[];
  confirmationToken: string | null;
}

// Prefixes of the lists that show a final amount besides the tasks: the orders list (with its flag), a contract's
// orders, and the contract itself (its remaining amount).
const ORDER_LIST_QUERIES = ["getOrdersForCompany", "getContractOrders", "getContractById"];

/**
 * Saves final quantities for the forms that enter them (the single task, the multiple task, the order drawer), with
 * the backend's plausibility check. A warning (APP 188) opens `warningModal`; confirming resends the same request
 * with the token, and a correction from the dialog resends the request with the new weights and no token.
 */
export const useFinalQuantitySave = ({ mode, taskId, orderLabels, close }: UseFinalQuantitySaveOptions) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { warning, request, opened, catchWarning, close: closeWarning } = useFinalAmountWarning<FinalQuantityEntry[]>();
  const [fieldErrors, setFieldErrors] = useState<Record<number, string>>({});
  // Set the moment a request is sent. `mutation.isPending` only follows on the next render, so two activations in
  // one tick (a double click) would both see it false.
  const inFlight = useRef(false);
  // Orders a batch has already saved. The weight orders go before the others, so when the others then fail, a retry
  // must not send the weight orders again: they are final, and the backend would refuse the whole request.
  const saved = useRef(new Set<number>());

  // Resolves once the open tasks are refetched, as the task UI must not show a task that is done. The orders refetch in
  // the background, so Save does not keep spinning until every order list has come back.
  const refresh = () => {
    ORDER_LIST_QUERIES.forEach((queryKey) => void queryClient.invalidateQueries({ queryKey: [queryKey] }));
    // A batch's task closes right after, so the orders it shows are only marked stale, not fetched again for nothing.
    void queryClient.invalidateQueries(
      mode === "batch" ? { queryKey: ["getOrderById"], refetchType: "none" } : { queryKey: ["getOrderById"] }
    );
    return queryClient.invalidateQueries({ queryKey: ["getOpenTasks"] });
  };

  const mutation = useMutation({
    mutationFn: async ({ entries, confirmationToken }: SaveVariables) => {
      if (mode === "single") {
        const [entry] = entries;
        if (!entry) return;
        if (isWeightEntry(entry)) {
          await postFinalQuantity(entry.orderId, toWeightQuantity(entry.weight), confirmationToken, taskId);
        } else {
          await postFinalAmount(entry.orderId, entry.amount, taskId);
        }
        return;
      }

      // The weight orders go first, and the others only once those are saved: a warning on the weight orders
      // must not leave the others already saved.
      const weightEntries = entries.filter(isWeightEntry);
      const amountEntries = entries.filter(isAmountEntry);
      if (weightEntries.length > 0) {
        await postFinalQuantities(
          weightEntries.map(({ orderId, weight }) => ({ orderId, quantity: toWeightQuantity(weight) })),
          confirmationToken,
          // The last request completes the task, so it stays open for a retry when the other orders fail.
          amountEntries.length > 0 ? undefined : taskId
        );
        weightEntries.forEach(({ orderId }) => saved.current.add(orderId));
      }
      if (amountEntries.length > 0) {
        await postFinalAmounts(
          { orderamounts: amountEntries.map(({ orderId, amount }) => ({ orderId, amount })) },
          taskId
        );
      }
    },
    onSuccess: async () => {
      closeWarning();
      setFieldErrors({});
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:orders.finalAmountSet"))
      );
      await refresh();
      close();
    },
    onError: async (error, { entries }) => {
      // The legacy endpoints cannot carry a token, so a warning on a request without a weight could never be confirmed.
      if (entries.some(isWeightEntry) && catchWarning(error, entries)) return;

      // Any other answer ends the request the warning was about, so a dialog still open has nothing left to decide.
      closeWarning();
      const answer = getFinalAmountError(error);
      // A 189 names the order it refused. The form already checks the backend's rules for the number, so this is in
      // practice an order without exactly one delivery to weigh: the message is about the order, not the number. For
      // a problem with the batch itself (an empty batch, a duplicate order) the backend names order 0, which no row
      // has, so that falls through to the generic error.
      if (
        answer?.kind === "invalidQuantity" &&
        answer.orderId !== undefined &&
        entries.some((entry) => isWeightEntry(entry) && entry.orderId === answer.orderId)
      ) {
        setFieldErrors({ [answer.orderId]: "validation:weight.rejected" });
        return;
      }
      if (answer?.kind === "invalidConfiguration") {
        notifications.show(
          ErrorNotificationObj(
            t("notifications:generic.error"),
            t("notifications:orders.finalAmountCheckMisconfigured")
          )
        );
        return;
      }
      if (answer?.kind === "stateConflict") {
        notifications.show(
          ErrorNotificationObj(t("notifications:generic.error"), t("notifications:orders.finalAmountStateConflict"))
        );
        await refresh();
        close();
        return;
      }
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:orders.finalAmountSetError"))
      );
      // The toast is gone after a few seconds, so a single order's field keeps saying that the save failed.
      if (mode === "single" && entries[0]) {
        setFieldErrors({ [entries[0].orderId]: "notifications:orders.finalAmountSetError" });
      }
      await queryClient.invalidateQueries({ queryKey: ["getOpenTasks"] });
    },
    // Runs after onSuccess or onError has finished, which is when `isPending` turns false too.
    onSettled: () => {
      inFlight.current = false;
    },
  });

  // One request at a time: a second one would send the same token again, or an older request next to a newer one.
  const send = (entries: FinalQuantityEntry[], confirmationToken: string | null) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setFieldErrors({});
    mutation.mutate({ entries: entries.filter((entry) => !saved.current.has(entry.orderId)), confirmationToken });
  };

  const warningModal: FinalAmountWarningModalProps = {
    warning,
    opened,
    // The dialog's labels, keyed the way its entries are.
    labels: Object.fromEntries(
      Object.entries(orderLabels ?? {}).map(([orderId, label]) => [assessmentKey({ orderId: Number(orderId) }), label])
    ),
    isPending: mutation.isPending,
    onCancel: closeWarning,
    onConfirm: () => {
      if (!warning || !request) return;
      send(request, warning.confirmationToken);
    },
    onResubmit: (weights) => {
      if (!warning || !request) return;
      send(
        request.map((entry) => {
          const edited = isWeightEntry(entry) ? weights[assessmentKey({ orderId: entry.orderId })] : undefined;
          return edited ? { ...entry, weight: edited } : entry;
        }),
        null
      );
    },
  };

  return {
    save: (entries: FinalQuantityEntry[]) => send(entries, null),
    isPending: mutation.isPending,
    /**
     * Why an order was not saved, as translation keys by order id: a 189, or any failure of a single order's save.
     * Cleared by the next save.
     */
    fieldErrors,
    warningModal,
  };
};
